import { ShipInfo } from '@ais-ui/websockets';
import { useGetShipByMmsi } from '@ais-ui/api';
import { LoadingSpinner, Separator } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import React from 'react';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { extractMmsiFromShipInfo } from '../../utils/extractMmsiFromShipInfo';
import { ErrorDisplay } from '../../../components/ErrorDisplay';
import { usePermissions } from '../../Permissions/PermissionsProvider';

interface EngineItemProps {
  label: string;
  value: string;
}

const EngineInformationItem = ({ label, value }: EngineItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

interface TonnageItemProps {
  label: string;
  value: string;
}

const TonnageInformationItem = ({ label, value }: TonnageItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

interface MiscellaneousItemProps {
  label: string;
  value: string;
}

const MiscellaneousInformationItem = ({
  label,
  value,
}: MiscellaneousItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

const Engine = ({ advancedVessel }: { advancedVessel: any }) => {
  const { messages } = useLocalization();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEngineTypeLabel}
        value={advancedVessel?.engineType}
      ></EngineInformationItem>
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEnginePropulsionPowerLabel}
        value={advancedVessel?.enginePropulsionPowerKw}
      ></EngineInformationItem>
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEnginePropulsionTypeLabel}
        value={advancedVessel?.enginePropulsionType}
      ></EngineInformationItem>
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEnginePropulsionMethodLabel}
        value={advancedVessel?.enginePropulsionMethod}
      ></EngineInformationItem>
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEngineMakerLabel}
        value={advancedVessel?.engineMaker}
      ></EngineInformationItem>
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEngineYearLabel}
        value={advancedVessel?.engineYear}
      ></EngineInformationItem>
    </div>
  );
};

const Tonnage = ({ advancedVessel }: { advancedVessel: any }) => {
  const { messages } = useLocalization();
  return (
    <div className="grid grid-cols-2 gap-4">
      <TonnageInformationItem
        label={messages.shipDetailsAdvancedDeadWeightTonnageLabel}
        value={
          advancedVessel?.deadweightTonnage
            ? `${advancedVessel.deadweightTonnage} tonnes`
            : '-'
        }
      ></TonnageInformationItem>
      <TonnageInformationItem
        label={messages.shipDetailsAdvancedGrossTonnageLabel}
        value={
          advancedVessel?.grossTonnage
            ? `${advancedVessel.grossTonnage} GT`
            : '-'
        }
      ></TonnageInformationItem>
      <TonnageInformationItem
        label={messages.shipDetailsAdvancedNetTonnageLabel}
        value={
          advancedVessel?.netTonnage ? `${advancedVessel.netTonnage} NT` : '-'
        }
      ></TonnageInformationItem>
    </div>
  );
};

const Miscellaneous = ({ advancedVessel }: { advancedVessel: any }) => {
  const { formatDateWithTimeZone } = useDateTimeFormat();
  const { messages } = useLocalization();
  return (
    <div className="grid grid-cols-2 gap-4">
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedDisplacementLabel}
        value={advancedVessel?.displacement}
      ></MiscellaneousInformationItem>
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedFormerVesselNameLabel}
        value={advancedVessel?.formerVesselName}
      ></MiscellaneousInformationItem>
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedBuildPlaceLabel}
        value={advancedVessel?.buildPlace}
      ></MiscellaneousInformationItem>
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedBuildYearLabel}
        value={advancedVessel?.buildYear}
      ></MiscellaneousInformationItem>
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedBuilderLabel}
        value={advancedVessel?.builder}
      ></MiscellaneousInformationItem>
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedKeelLaidDateLabel}
        value={formatDateWithTimeZone(advancedVessel?.keelLaidDate)}
      ></MiscellaneousInformationItem>
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedVesselMaterialLabel}
        value={advancedVessel?.vesselMaterial}
      ></MiscellaneousInformationItem>
    </div>
  );
};

interface ExtendedVesselAdvancedInformation {
  extendedVesselInfo: VesselResult;
}

export const ExtendedVesselAdvancedInformation = ({
  extendedVesselInfo,
}: ExtendedVesselAdvancedInformation) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Engine</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Engine advancedVessel={extendedVesselInfo?.advancedVessel}></Engine>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Tonnage</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Tonnage advancedVessel={extendedVesselInfo?.advancedVessel}></Tonnage>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Miscellaneous</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Miscellaneous
          advancedVessel={extendedVesselInfo?.advancedVessel}
        ></Miscellaneous>
      </div>
    </div>
  );
};

interface AdvancedVesselInformationProps {
  shipInfo: ShipInfo;
}

export const AdvancedVesselInformation = ({
  shipInfo,
}: AdvancedVesselInformationProps) => {
  const permissions = usePermissions();
  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const {
    data: extendedVesselInfo,
    isLoading,
    isError,
  } = useGetShipByMmsi(
    extractMmsiFromShipInfo(shipInfo),
    hasShipDbPermissions
  );

  if (isLoading) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  if (isError) {
    return <ErrorDisplay className="h-full pt-60"></ErrorDisplay>;
  }

  if (!extendedVesselInfo) {
    return <ErrorDisplay className="h-full pt-60"></ErrorDisplay>;
  }

  return (
    <ExtendedVesselAdvancedInformation
      extendedVesselInfo={extendedVesselInfo}
    ></ExtendedVesselAdvancedInformation>
  );
};
